import {
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CodeIcon,
  FolderSimpleIcon,
  LayoutIcon,
  ListIcon,
  NoteIcon,
  NotepadIcon,
  SquaresFourIcon,
  TicketIcon,
  TrendUpIcon,
  XIcon,
} from '@phosphor-icons/react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import { Color } from 'src/constants';
import { color } from 'src/constants/color.css';
import { useClientData } from 'src/contexts';
import { useAuthUser, useMyUserClient } from 'src/hooks';
import { Model } from 'src/models';
import { isAgency } from 'src/reducers/auth';

import Dropdown from './Dropdown';
import DropdownHeading from './DropdownHeading';
import DropdownItem from './DropdownItem';
import { RouterLink, RouterNavLink } from './RouterLink';
import Text from './Text';
import TimerSideMenu from './TimerSideMenu';

type UserToCompany = Api.User.V_Self['userToCompanies'][number];

export default function SideNav() {
  const authUser = useAuthUser();
  const myUser = useMyUserClient();
  const { agencyCompanies, clientCompanies, user } = useClientData();
  const allCompanies = [...clientCompanies, ...agencyCompanies];

  const [menuOpen, setMenuOpen] = React.useState(false);
  // Mobile-only: swaps the whole drawer over to the clients list (see design).
  const [clientsView, setClientsView] = React.useState(false);

  const closeMenu = () => {
    setMenuOpen(false);
    setClientsView(false);
  };

  const location = useLocation();

  // Close the drawer on any navigation (only has an effect on mobile).
  React.useEffect(() => {
    setMenuOpen(false);
    setClientsView(false);
  }, [location.pathname]);

  const matches = location.pathname.match(/^\/company\/([^/]+)/);
  const selectedCompanySlug = matches ? matches[1] : null;
  const selectedCompany = allCompanies.find(
    (c) => c.slug === selectedCompanySlug
  );
  const selectedCompanyWithFallback = selectedCompany || allCompanies[0];

  // Internal (agency) users see a top-level menu (Operations + Clients list)
  // when not inside a specific company; picking a client drops into the
  // company-scoped menu.
  const isTopLevel = isAgency(authUser) && !selectedCompanySlug;

  const utc: UserToCompany | null | undefined = user.userToCompanies.find(
    (utc_: UserToCompany) =>
      selectedCompany && utc_.company?.id === selectedCompany.id
  );

  const dashboardPath = Model.User.getDashboardPath(myUser);

  const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(
    0
  )}`.toUpperCase();

  return (
    <>
      {/* Mobile overlay */}
      {menuOpen && <html.div style={styles.menuMask} onClick={closeMenu} />}

      {/* Mobile header bar */}
      <html.header style={styles.mobileHeader}>
        <html.div style={styles.mobileHeaderLeft}>
          <html.button
            style={styles.mobileHeaderToggle}
            onClick={() =>
              clientsView ? setClientsView(false) : setMenuOpen(!menuOpen)
            }
            aria-label={
              clientsView ? 'Back' : menuOpen ? 'Close menu' : 'Open menu'
            }
          >
            {clientsView ? (
              <CaretLeftIcon size={20} color={Color.neutral80} />
            ) : menuOpen ? (
              <XIcon size={20} color={Color.neutral80} />
            ) : (
              <ListIcon size={20} color={Color.neutral80} />
            )}
          </html.button>
          <img src="/img/logo_new.svg" style={{ height: 12 }} />
          {selectedCompany && (
            <img
              src={`/img/companies/${selectedCompany.slug}.png`}
              style={{
                width: 32,
                height: 32,
                borderRadius: 4,
                objectFit: 'cover',
              }}
            />
          )}
        </html.div>
      </html.header>

      <html.nav style={[styles.sideNav, menuOpen && styles.sideNavOpen]}>
        <html.div style={styles.top}>
          {clientsView ? (
            <html.div style={styles.clientsList}>
              <Text as="p" style={styles.clientsHeading}>
                Clients
              </Text>
              {agencyCompanies
                .filter((c) => !c.archivedAt)
                .map((company) => {
                  const isCurrent = company.id === selectedCompany?.id;
                  return (
                    <RouterLink
                      key={company.id}
                      to={`/company/${company.slug}`}
                      onClick={closeMenu}
                      {...css.props(
                        styles.clientRow,
                        isCurrent && styles.clientRowActive
                      )}
                    >
                      <img
                        src={`/img/companies/${company.slug}.png`}
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 4,
                          objectFit: 'cover',
                        }}
                      />
                      <Text style={styles.clientName}>{company.name}</Text>
                    </RouterLink>
                  );
                })}
            </html.div>
          ) : (
            <>
              {/* m10c logo — internal users land on the top-level menu */}
              <RouterLink
                to={dashboardPath ?? (isAgency(authUser) ? '/operations' : '/')}
              >
                <html.div style={styles.logoArea}>
                  {!isAgency(authUser) && selectedCompanyWithFallback && (
                    <img
                      src={`/img/companies/${selectedCompanyWithFallback.slug}.png`}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 4,
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  <img src="/img/logo_new.svg" style={{ height: 18 }} />
                </html.div>
              </RouterLink>

              {/* Timers are the agency's own, and /work-dones/recent is
                  ROLE_AGENCY only, so clients never see this. */}
              {isAgency(authUser) && <TimerSideMenu />}

              <html.div style={styles.menuScroll}>
                {isTopLevel ? (
                  <>
                    {/* Top-level internal menu: Dashboard, Operations and the
                    Clients list. */}
                    <html.div
                      style={[styles.navSection, styles.navSectionBorder]}
                    >
                      {dashboardPath && (
                        <NavItem
                          to={dashboardPath}
                          icon={SquaresFourIcon}
                          label="Dashboard"
                        />
                      )}
                      <NavItem
                        to="/projects"
                        icon={FolderSimpleIcon}
                        label="Projects"
                      />
                      {myUser.agencyRole && (
                        <NavItem
                          to="/operations"
                          icon={NotepadIcon}
                          label="Operations"
                        />
                      )}
                    </html.div>
                    <html.div style={styles.navSection}>
                      <Text style={styles.sectionHeading}>Clients</Text>
                      {agencyCompanies
                        .filter((c) => !c.archivedAt)
                        .map((company) => (
                          <RouterNavLink
                            key={company.id}
                            to={`/company/${company.slug}`}
                          >
                            {({ isActive }) => (
                              <html.div
                                style={[
                                  styles.navItem,
                                  isActive && styles.navItemActive,
                                ]}
                              >
                                <img
                                  src={`/img/companies/${company.slug}.png`}
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 4,
                                    objectFit: 'cover',
                                  }}
                                />
                                <Text style={styles.navItemLabel}>
                                  {company.name}
                                </Text>
                              </html.div>
                            )}
                          </RouterNavLink>
                        ))}
                    </html.div>
                  </>
                ) : (
                  <>
                    {/* Company picker — agency only; shown on all pages (uses the
                    current or first company). Clients don't get a selector. */}
                    {isAgency(authUser) && selectedCompanyWithFallback && (
                      <html.div style={styles.companyPicker}>
                        <Dropdown
                          trigger={
                            <html.div style={styles.companyTrigger}>
                              <img
                                src={`/img/companies/${selectedCompanyWithFallback.slug}.png`}
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 4,
                                  objectFit: 'cover',
                                }}
                              />
                              <Text style={styles.companyName}>
                                {selectedCompanyWithFallback.name}
                              </Text>
                              <CaretDownIcon
                                size={16}
                                color={Color.neutral40}
                              />
                            </html.div>
                          }
                        >
                          <DropdownHeading>Clients</DropdownHeading>
                          {agencyCompanies
                            .filter((c) => !c.archivedAt)
                            .map((company) => (
                              <DropdownItem
                                key={company.id}
                                to={`/company/${company.slug}`}
                                name={
                                  <Text
                                    typeface="body.regular"
                                    color="neutral100"
                                  >
                                    {company.name}
                                  </Text>
                                }
                                left={
                                  <img
                                    src={`/img/companies/${company.slug}.png`}
                                    style={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: 4,
                                      marginRight: 10,
                                    }}
                                  />
                                }
                              />
                            ))}
                          {/* The picker is agency-only now, so clients never reach this.
                          Kept in case clients with multiple companies should switch:
                      <DropdownHeading>My Companies</DropdownHeading>
                      {clientCompanies.map((company) => (
                        <DropdownItem
                          key={company.id}
                          name={
                            <Text typeface="body.regular" color="neutral100">
                              {company.name}
                            </Text>
                          }
                          left={
                            <img
                              src={`/img/companies/${company.slug}.png`}
                              style={{ width: 24, height: 24, borderRadius: 4, marginRight: 10 }}
                            />
                          }
                          onClick={() => navigate(`/company/${company.slug}`)}
                        />
                      ))}
                      */}
                        </Dropdown>
                      </html.div>
                    )}

                    {/* Primary nav section */}
                    <html.div
                      style={[styles.navSection, styles.navSectionBorder]}
                    >
                      {dashboardPath && (
                        <NavItem
                          to={dashboardPath}
                          icon={SquaresFourIcon}
                          label="Dashboard"
                        />
                      )}
                      {selectedCompanyWithFallback && (
                        <NavItem
                          to={`/company/${selectedCompanyWithFallback.slug}/tickets`}
                          icon={TicketIcon}
                          label="Maintenance"
                        />
                      )}
                      {/* Service Orders hidden — page doesn't exist yet
                  {selectedCompanyWithFallback && (
                    <NavItem
                      to={`/company/${selectedCompanyWithFallback.slug}/releases`}
                      icon={NoteIcon}
                      label="Service Orders"
                    />
                  )}
                  */}
                      {myUser.agencyRole && (
                        <NavItem
                          to="/operations"
                          icon={NotepadIcon}
                          label="Operations"
                        />
                      )}
                      {selectedCompanyWithFallback &&
                        (myUser.agencyRole === 'owner' || utc?.finance) && (
                          <NavItem
                            to={`/company/${selectedCompanyWithFallback.slug}/billing`}
                            icon={NoteIcon}
                            label="Plan"
                          />
                        )}
                    </html.div>

                    {/* Secondary nav section */}
                    <html.div style={styles.navSection}>
                      {/* Mobile company selector — opens the clients list; the
                      desktop dropdown above is hidden on small screens. */}
                      {isAgency(authUser) && selectedCompanyWithFallback && (
                        <html.div
                          style={styles.mobileCompanySelector}
                          onClick={() => setClientsView(true)}
                        >
                          <img
                            src={`/img/companies/${selectedCompanyWithFallback.slug}.png`}
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 4,
                              objectFit: 'cover',
                            }}
                          />
                          <Text style={styles.companyName}>
                            {selectedCompanyWithFallback.name}
                          </Text>
                          <CaretRightIcon size={12} color={Color.neutral40} />
                        </html.div>
                      )}
                      {selectedCompanyWithFallback && (
                        <NavGroup
                          icon={LayoutIcon}
                          label="Design"
                          basePath={`/company/${selectedCompanyWithFallback.slug}`}
                          matchPaths={['/design', '/wishlist']}
                        >
                          <NavChildItem
                            to={`/company/${selectedCompanyWithFallback.slug}/design/resources`}
                            label="Resources"
                          />
                          {/* Assets & Specs hidden until those pages are built —
                          this branch only ships Design → Resources
                      <NavChildItem
                        to={`/company/${selectedCompanyWithFallback.slug}/design/assets`}
                        label="Assets"
                      />
                      <NavChildItem
                        to={`/company/${selectedCompanyWithFallback.slug}/design/specs`}
                        label="Specs"
                      />
                      */}
                          <NavChildItem
                            to={`/company/${selectedCompanyWithFallback.slug}/wishlist`}
                            label="Wishlist"
                          />
                        </NavGroup>
                      )}
                      {selectedCompanyWithFallback && (
                        <NavGroup
                          icon={CodeIcon}
                          label="Development"
                          basePath={`/company/${selectedCompanyWithFallback.slug}`}
                          matchPaths={[
                            '/development/environments',
                            '/uptime',
                            '/bi/data',
                            '/releases',
                          ]}
                        >
                          <NavChildItem
                            to={`/company/${selectedCompanyWithFallback.slug}/development/environments`}
                            label="Environments"
                          />
                          {selectedCompanyWithFallback.betterUptimeStatusPageId && (
                            <NavChildItem
                              to={`/company/${selectedCompanyWithFallback.slug}/uptime`}
                              label="Uptime"
                            />
                          )}
                          {/* KPI Dashboard moved to the Growth group */}
                          {selectedCompanyWithFallback.featureBi && (
                            <NavChildItem
                              to={`/company/${selectedCompanyWithFallback.slug}/bi/data`}
                              label="Data Sets"
                            />
                          )}
                          <NavChildItem
                            to={`/company/${selectedCompanyWithFallback.slug}/releases`}
                            label="Releases"
                          />
                        </NavGroup>
                      )}
                      {selectedCompanyWithFallback?.featureBi && (
                        <NavGroup
                          icon={TrendUpIcon}
                          label="Growth"
                          basePath={`/company/${selectedCompanyWithFallback.slug}/bi/kpi`}
                        >
                          <NavChildItem
                            to={`/company/${selectedCompanyWithFallback.slug}/bi/kpi`}
                            label="KPI Dashboard"
                          />
                        </NavGroup>
                      )}
                    </html.div>
                  </>
                )}
              </html.div>
            </>
          )}
        </html.div>

        {/* Account */}
        <html.div style={styles.accountSection}>
          <RouterLink to="/profile">
            <html.div style={styles.accountLink}>
              <html.div style={styles.accountAvatar}>
                <Text style={styles.accountInitials}>{initials}</Text>
              </html.div>
              <Text style={styles.accountLabel}>Account</Text>
            </html.div>
          </RouterLink>
        </html.div>
      </html.nav>
    </>
  );
}

// --- NavItem sub-component ---

type NavItemProps = {
  to: string;
  icon: React.ComponentType<{ size: number; color?: string }>;
  label: string;
  end?: boolean;
};

function NavItem({ to, icon: Icon, label, end }: NavItemProps) {
  return (
    <RouterNavLink to={to} end={end}>
      {({ isActive }) => (
        <html.div style={[styles.navItem, isActive && styles.navItemActive]}>
          <html.div style={styles.navItemContent}>
            <Icon size={20} color={Color.neutral80} />
            <Text style={styles.navItemLabel}>{label}</Text>
          </html.div>
        </html.div>
      )}
    </RouterNavLink>
  );
}

// --- NavGroup (collapsible section) ---

type NavGroupProps = {
  icon: React.ComponentType<{ size: number; color?: string }>;
  label: string;
  basePath: string;
  matchPaths?: string[];
  children: React.ReactNode;
};

function NavGroup({
  icon: Icon,
  label,
  basePath,
  matchPaths,
  children,
}: NavGroupProps) {
  const location = useLocation();

  // Match on full path segments so e.g. "/bi" doesn't match "/billing"
  const matchesPath = (full: string) =>
    location.pathname === full || location.pathname.startsWith(full + '/');
  const isActive = matchPaths
    ? matchPaths.some((p) => matchesPath(basePath + p))
    : matchesPath(basePath);

  // Groups start open; the user can collapse them to free up space.
  const [open, setOpen] = React.useState(true);

  return (
    <>
      <html.div
        style={[styles.navItem, isActive && styles.navItemActive]}
        onClick={() => setOpen((o) => !o)}
      >
        <html.div style={styles.navItemContent}>
          <Icon size={20} color={Color.neutral80} />
          <Text style={styles.navItemLabel}>{label}</Text>
        </html.div>
        {open ? (
          <CaretDownIcon size={12} color={Color.neutral40} />
        ) : (
          <CaretRightIcon size={12} color={Color.neutral40} />
        )}
      </html.div>
      {open && children}
    </>
  );
}

// --- NavChildItem (indented child link) ---

type NavChildItemProps = {
  to: string;
  label: string;
};

function NavChildItem({ to, label }: NavChildItemProps) {
  return (
    <RouterNavLink to={to}>
      {({ isActive }) => (
        <html.div
          style={[styles.navChildItem, isActive && styles.navItemActive]}
        >
          <Text style={styles.navItemLabel}>{label}</Text>
        </html.div>
      )}
    </RouterNavLink>
  );
}

// --- Styles ---

export const SIDE_NAV_WIDTH = 200;
// The mobile drawer is wider than the desktop rail; capped at 90% of the screen.
const MOBILE_NAV_WIDTH = 300;

const styles = css.create({
  sideNav: {
    position: 'fixed',
    top: 0,
    left: {
      default: 0,
      // slide off-screen on mobile until opened
      '@media (max-width: 768px)': -MOBILE_NAV_WIDTH,
    },
    bottom: 0,
    width: {
      default: SIDE_NAV_WIDTH,
      '@media (max-width: 768px)': MOBILE_NAV_WIDTH,
    },
    maxWidth: {
      default: null,
      '@media (max-width: 768px)': '90%',
    },
    paddingTop: {
      default: 0,
      // clear the fixed mobile header
      '@media (max-width: 768px)': 56,
    },
    backgroundColor: color.neutral0,
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderRightColor: color.neutral20,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    zIndex: 610,
    transition: 'left 0.25s',
  },
  sideNavOpen: {
    left: {
      default: 0,
      '@media (max-width: 768px)': 0,
    },
  },
  top: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    // scroll long content (e.g. the clients list) while Account stays pinned
    overflowY: 'auto',
  },
  menuScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  logoArea: {
    // replaced by the mobile header bar on small screens
    display: {
      default: 'flex',
      '@media (max-width: 768px)': 'none',
    },
    alignItems: 'center',
    gap: 10,
    paddingTop: 24,
    paddingBottom: 20,
    paddingLeft: 24,
    paddingRight: 24,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
  },
  companyPicker: {
    // replaced by the mobile header bar on small screens
    display: {
      default: 'block',
      '@media (max-width: 768px)': 'none',
    },
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 16,
    paddingRight: 16,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
  },
  companyTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
  },
  mobileCompanySelector: {
    // desktop uses the dropdown above; this row only shows on mobile
    display: {
      default: 'none',
      '@media (max-width: 768px)': 'flex',
    },
    alignItems: 'center',
    gap: 8,
    paddingBlock: 10,
    paddingInline: 12,
    borderRadius: 8,
    cursor: 'pointer',
  },
  companyName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 1.3,
    color: color.neutral100,
  },
  clientsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 12,
    paddingRight: 12,
  },
  clientsHeading: {
    fontSize: 12,
    lineHeight: 1.4,
    color: color.neutral40,
    paddingBlock: 4,
    paddingInline: 12,
  },
  clientRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingBlock: 12,
    paddingInline: 16,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  clientRowActive: {
    backgroundColor: color.neutral10,
  },
  clientName: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 1.4,
    color: color.neutral100,
  },
  navSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 12,
    paddingRight: 12,
  },
  navSectionBorder: {
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
  },
  sectionHeading: {
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 1.4,
    color: color.neutral40,
    paddingBlock: 8,
    paddingInline: 12,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingBlock: 10,
    paddingInline: 12,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  navItemActive: {
    backgroundColor: color.neutral10,
  },
  navItemContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  navChildItem: {
    display: 'flex',
    alignItems: 'center',
    paddingBlock: 10,
    paddingLeft: 48,
    paddingRight: 12,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  navItemLabel: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 1.4,
    color: color.neutral80,
    flex: 1,
  },
  accountSection: {
    paddingTop: {
      default: 24,
      '@media (max-width: 768px)': 20,
    },
    paddingBottom: {
      default: 24,
      '@media (max-width: 768px)': 20,
    },
    paddingLeft: 12,
    paddingRight: 12,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: color.neutral20,
  },
  accountLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 12,
    paddingRight: 12,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  accountAvatar: {
    width: 32,
    height: 32,
    borderRadius: 100,
    backgroundColor: color.neutral100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  accountInitials: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 1.4,
    color: color.neutral0,
  },
  accountLabel: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 1.4,
    color: color.neutral80,
  },
  menuMask: {
    position: 'fixed',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 600,
  },
  mobileHeader: {
    display: {
      default: 'none',
      '@media (max-width: 768px)': 'flex',
    },
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: 56,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 16,
    paddingRight: 16,
    backgroundColor: color.neutral0,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    zIndex: 620,
  },
  mobileHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  mobileHeaderToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 0,
  },
});
